import api from 'services/api';
import { REJECT, array, num, numeric, object, refuseDegraded, str } from 'validation';
import { readResponse } from 'api/shared/readResponse';

// A PROJECTION CONTRACT over `GET /node_definitions` — see ../../README.md. Self-contained on
// purpose: its own type, its own schema, shared with nothing. The full contract for
// this route is `getNodeDefinitions`, and a picker reads three of its fields.
//
// Declaring only those is what lets a picker refuse a degraded answer without
// refusing on drift it does not care about. `object()` ignores the rest of the
// payload, so nothing else is parsed and nothing else can warn — and that stays
// true when `getNodeDefinitions` grows to the full response in its E-S pass, which is why
// this does not simply read through it.
//
// It shares the route with `getNodeDefinitions` today; a slim backend read — less bandwidth,
// and the server-side filtering some of these lack — would replace the request and
// leave this contract standing.

/** A node definition as a picker deals in it. */
export interface NodeDefinitionSummary {
  readonly node_definition_id: number;
  // Deliberately stricter than the nullable column: a row's purpose is to address
  // the record, which is done by `zone_name/name`. See ./getNodeDefinition.ts.
  readonly name: string;
  readonly zone_name: string;
}

const summarySchema = object<NodeDefinitionSummary>({
  node_definition_id: num(REJECT),
  name: str(),
  zone_name: str(),
});

interface NodeDefinitionsSearchResponse {
  readonly nodes: readonly NodeDefinitionSummary[];
  readonly pagination: { readonly total_records: number };
}

const responseSchema = object<NodeDefinitionsSearchResponse>({
  nodes: array(summarySchema),
  // COUNT-derived totals arrive as numeric strings from Postgres.
  pagination: object<{ readonly total_records: number }>({ total_records: numeric(REJECT) }),
});

export interface SearchNodeDefinitionsParams {
  readonly limit: number;
  readonly search?: string;
  readonly zone_name?: string;
  readonly type?: string;
  readonly node_definition_id?: number;
}

/** One page of options, with the server-side count of all matches. */
export interface NodeDefinitionsPage {
  readonly items: readonly NodeDefinitionSummary[];
  readonly total: number;
}

// In-flight de-duplication, keyed on the exact query: independent callers that ask
// the same question at the same time share one request rather than one each.
// Nothing is cached past settlement — asking again later is a genuinely new
// question, since the underlying records change under us.
const inFlight = new Map<string, Promise<NodeDefinitionsPage>>();

// Key on the query's entries, sorted, so two callers that build the same query with
// their keys in a different order still share one request.
const queryKey = (query: SearchNodeDefinitionsParams): string =>
  JSON.stringify(
    Object.entries(query)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : 1))
  );

/**
 * One page of node definitions matching `params`, ordered by name.
 *
 * Name order is fixed here rather than left to callers, so that no caller can
 * present an unordered list. The backend sorts this one case-insensitively
 * (`getNodeDefinitions.js` maps `name` to `LOWER(nd.name), nd.name`), unlike the
 * `COLLATE "C"` byte order the lock and event searches get.
 *
 * Collapses with `refuseDegraded`, and that is a property of this contract rather
 * than a policy imposed on an unknown consumer: an option missing from the list is
 * one the user cannot pick, and one they picked before no longer resolves. A
 * partial list of values is not a list of values.
 *
 * `undefined` params are dropped by axios, so omitting one widens the query rather
 * than filtering on empty.
 */
export const searchNodeDefinitions = async (params: SearchNodeDefinitionsParams): Promise<NodeDefinitionsPage> => {
  const key = queryKey(params);
  const pending = inFlight.get(key);
  if (pending !== undefined) return pending;

  const request = api
    .get('/node_definitions', { params: { ...params, sortColumn: 'name', sortOrder: 'ASC' } })
    .then((res) => refuseDegraded(readResponse(res.data, { schema: responseSchema, source: 'searchNodeDefinitions' })))
    .then(({ nodes, pagination }) => ({ items: nodes, total: pagination.total_records }))
    .finally(() => inFlight.delete(key));
  inFlight.set(key, request);
  return request;
};
